import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { clearSortAndFilter, fetchActivities, setSortAndFilter } from 'component/page/search/store/search';
import {
    EmptyResultInfo,
    selectFilterState,
    selectMatrixFromSearchState,
    selectResultInfoFromSearchState,
    selectSearchStateIsLoading,
    selectSortState,
} from 'component/page/search/store/search.selectors';
import { Pagination } from 'gql/autogen/Pagination';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesSortAndFilterApplyButtonClickEventActivitiesManifestV4,
    ActivitiesSortAndFilterClearButtonClickEventActivitiesManifestV4,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { selectConfigFromBootstrap, selectExperimentsFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { FilterState } from 'component/common/Filter/Filter.types';
import { Sort } from 'gql/autogen/Sort';
import {
    ActivitiesSortAndFilterApplyButtonClickEventActivitiesFieldsV4,
    ActivitiesSortAndFilterClearButtonClickEventActivitiesFieldsV4,
} from '@agoda/analytics-messages-definition-activities/events.split';
import { BootstrapConfig, CmsValue } from 'core/type/BootstrapConfig.types';
import { SortMatrix } from 'gql/autogen/SortMatrix';
import { FilterMatrix } from 'gql/autogen/FilterMatrix';
import { isB, isOn } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';
import { SortAndFilterContentVariant } from 'component/page/search/SortAndFilter/SortAndFilterContent.selectors';
import { FeatureName } from 'core/constant/FeatureName';

export interface OwnProps {
    visible: boolean;
    onClose: () => void;
    onClickBack: () => void;
    cms: CmsValue;
}

export interface StateProps {
    sort: Sort;
    filter: FilterState;
    sortMatrix: SortMatrix;
    filterMatrix: FilterMatrix;
    page: Pagination;
    config: BootstrapConfig;
    isLoading: boolean;
    isDroneSheet: boolean;
    isSubcategoryFilterEnabled: boolean;
    isFilterWithoutUpdatingHistory: boolean;
}

export interface DispatchProps {
    setSortAndFilter: (page: Pagination, sort: Sort, filter: FilterState, variant: SortAndFilterContentVariant) => void;
    clearSortAndFilter: (variant: SortAndFilterContentVariant) => void;
}

export type Props = OwnProps & StateProps & DispatchProps;
export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectSearchStateIsLoading,
    selectResultInfoFromSearchState,
    selectSortState,
    selectFilterState,
    selectMatrixFromSearchState,
    selectExperimentsFromBootstrap,
    selectFeatureBehaviorFromBootstrap,
    (config, isLoading, resultInfo, sort, filter, matrix, experiments, feature): StateProps => {
        const sortMatrix = matrix?.sort || {};
        const filterMatrix = matrix?.filter || {};
        const page = resultInfo.pagination || EmptyResultInfo.pagination;
        const isDroneSheet = isB(ExperimentId.ACT_4452, experiments);
        const isSubcategoryFilterEnabled = isOn(FeatureName.SubcategoryFilter, config);

        return {
            config,
            sort,
            filter,
            page,
            sortMatrix,
            filterMatrix,
            isLoading,
            isDroneSheet,
            isSubcategoryFilterEnabled,
            isFilterWithoutUpdatingHistory: feature.FilterWithoutUpdatingHistory,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    setSortAndFilter: (page: Pagination, sort: Sort, filter: FilterState, variant: SortAndFilterContentVariant) => {
        trackElementEvent<ActivitiesSortAndFilterApplyButtonClickEventActivitiesFieldsV4>(ActivitiesSortAndFilterApplyButtonClickEventActivitiesManifestV4.ids, {
            is_sidebar: variant === SortAndFilterContentVariant.Sidebar,
            is_filter_bottom_sheet: variant === SortAndFilterContentVariant.CategoryBottomSheet,
        });
        dispatch(setSortAndFilter({ sort, filter }));

        fetchActivities(1, page.size);
    },
    clearSortAndFilter: (variant: SortAndFilterContentVariant) => {
        trackElementEvent<ActivitiesSortAndFilterClearButtonClickEventActivitiesFieldsV4>(ActivitiesSortAndFilterClearButtonClickEventActivitiesManifestV4.ids, {
            is_sidebar: variant === SortAndFilterContentVariant.Sidebar,
            is_filter_bottom_sheet: variant === SortAndFilterContentVariant.CategoryBottomSheet,
        });
        dispatch(clearSortAndFilter());
        fetchActivities();
    },
});
